/* eslint-disable no-inline-styles/no-inline-styles */
/* eslint-disable react/no-array-index-key */
import { type FC, useEffect, useState } from 'react';

import Button from 'molecules/Button';
import Heading from 'molecules/Heading';
import Icon from 'molecules/Icon';
import Image from 'molecules/Image';
import RichText from 'molecules/RichText';
import Section from 'molecules/Section';
import { splitSectionProps } from 'molecules/Section/config';
import VideoCustom from 'molecules/VideoCustom';

import ScrollFadeSection from 'components/Animations/ScrollFadeSection';
import { SlideboxConfigProps } from 'components/Slidebox/config';

import { hasArrayValues } from 'utils/array/arrays';
import onKeyDown from 'utils/hooks/onKeyDown';

import type { SlideConfigProps } from 'components/Slidebox/config';

const SlideMedia: FC<{ slide: SlideConfigProps; isActive?: boolean; onPlay?: () => void }> = ({
  slide,
  isActive,
  onPlay,
}) => {
  if (slide?.mediaType === 'video' && slide?.video?.videoSrc) {
    return (
      <div className="w-full max-w-[720px]">
        <VideoCustom {...slide.video} key={slide.video.videoSrc} isActive={isActive} onPlay={onPlay} />
      </div>
    );
  }

  if (slide?.image?.file?.url) {
    return (
      <div
        className="relative flex w-full max-w-[720px] items-center justify-center overflow-visible bg-transparent"
        style={{
          minHeight: '400px',
          contain: 'layout',
          flexShrink: 0,
        }}
      >
        <Image
          url={slide.image.file.url}
          alt={slide.image.alt || ''}
          {...slide.image.file}
          className="w-full object-contain"
        />
      </div>
    );
  }

  return null;
};

const Slidebox: FC<SlideboxConfigProps> = props => {
  const { sectionProps, rest } = splitSectionProps(props);
  const { slides } = rest;
  const [count, setCount] = useState<number>(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) {
      return;
    }

    const interval = setInterval(() => {
      setCount(prevCount => prevCount + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [activeIndex, paused]);

  useEffect(() => {
    if (slides && count >= 10) {
      setActiveIndex(prev => (prev + 1 >= slides.length ? 0 : prev + 1));
      setCount(0);
    }
  }, [count, slides]);

  const handleSlide = (idx: number) => {
    setActiveIndex(idx);
    setCount(0);
    setPaused(false);
  };

  return (
    <Section {...sectionProps}>
      <div className="flex flex-col gap-12 text-gray-900 dark:text-white lg:gap-16">
        <Heading {...rest} />
        <div className="w-full px-0 lg:grid lg:grid-cols-2 lg:items-start lg:gap-16 lg:px-8">
          <ScrollFadeSection delay={0.2}>
            <div className="flex w-full flex-col">
              {hasArrayValues(slides) &&
                slides.map((item, idx) => {
                  const isActive = activeIndex === idx;

                  return (
                    <div key={idx}>
                      {/* Mobile: media above active slide's title */}
                      <div
                        className="grid transition-[grid-template-rows] duration-500 ease-in-out lg:hidden"
                        style={{ gridTemplateRows: isActive ? '1fr' : '0fr' }}
                      >
                        <div className="overflow-hidden">
                          <div className="mb-4 flex w-full items-start justify-center">
                            <SlideMedia slide={item} isActive={isActive} onPlay={() => setPaused(true)} />
                          </div>
                        </div>
                      </div>
                      <div
                        className={`relative flex cursor-pointer flex-col gap-1 p-4 lg:gap-2 lg:pl-6 ${
                          isActive ? '' : 'group'
                        }`}
                        onClick={() => handleSlide(idx)}
                        onKeyDown={e => onKeyDown(e, () => handleSlide(idx))}
                        role="button"
                        tabIndex={0}
                      >
                        <div className="flex items-center justify-between">
                          <div
                            className={`text-lg font-semibold transition-colors duration-300 sm:text-xl ${
                              isActive
                                ? 'text-gray-900 dark:text-white'
                                : 'text-gray-500 group-hover:text-gray-900 dark:group-hover:text-white'
                            }`}
                          >
                            {item?.title}
                          </div>
                          <Icon
                            icon="chevron-down"
                            className={`min-w-5 transition-all duration-300 ${
                              isActive
                                ? 'rotate-180 opacity-0'
                                : 'rotate-0 text-gray-500 opacity-100 group-hover:text-gray-900 dark:group-hover:text-white'
                            }`}
                          />
                        </div>
                        {/* Accordion content with smooth height animation */}
                        <div
                          className="grid transition-[grid-template-rows] duration-500 ease-in-out"
                          style={{ gridTemplateRows: isActive ? '1fr' : '0fr' }}
                        >
                          <div className="overflow-hidden">
                            <div
                              className="transition-opacity duration-500 ease-in-out"
                              style={{ opacity: isActive ? 1 : 0 }}
                            >
                              {item?.description && (
                                <RichText
                                  text={item.description}
                                  className="text-lg font-medium text-gray-700 dark:text-gray-300 sm:text-xl"
                                />
                              )}
                              {item?.button?.label && (
                                <Button hierarchy="linkColor" endIcon="arrow-right" {...item.button} className="mt-3" />
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="absolute left-0 top-0 h-full w-1 bg-gray-300" />
                        {isActive && (
                          <div className="absolute left-0 top-0 z-10 h-full w-1 animate-slideboxBorder bg-primary-700 dark:bg-primary-button-default" />
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </ScrollFadeSection>
          {/* Desktop: media in right column with crossfade */}
          <div className="hidden lg:grid">
            {hasArrayValues(slides) &&
              slides.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-start justify-center transition-opacity duration-500 ease-in-out"
                  style={{
                    gridArea: '1 / 1',
                    opacity: activeIndex === idx ? 1 : 0,
                    pointerEvents: activeIndex === idx ? 'auto' : 'none',
                  }}
                >
                  <SlideMedia slide={item} isActive={activeIndex === idx} onPlay={() => setPaused(true)} />
                </div>
              ))}
          </div>
        </div>
      </div>
    </Section>
  );
};

export default Slidebox;
